<template>
    <div class="label-picker" v-click-outside="close">
        <div class="lp-header">
            <p class="lp-title">{{ $t('dash.posts.labels.title') }}</p>
            <i class="fa-solid fa-xmark" @click="close"></i>
        </div>

        <div class="lp-search" v-if="!creating">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input
                type="text"
                v-model="search"
                :placeholder="$t('dash.posts.labels.search-placeholder')"
                @click.stop
            >
        </div>

        <div class="lp-list hide-scrollbar" v-if="!creating">
            <div
                class="lp-item"
                v-for="label in filteredLabels"
                :key="label.id"
            >
                <Checkbox
                    :id="`label-${label.id}`"
                    :prefixId="'labelPicker'"
                    :isChecked="isSelected(label.id)"
                    :remove="() => toggleLabel(label)"
                    :add="() => toggleLabel(label)"
                />
                <label :for="`labelPickerlabel-${label.id}`" class="lp-chip" :style="{ backgroundColor: label.color }">
                    <i v-if="parseIcon(label.icon) && parseIcon(label.icon).type === 'fa'" class="fa-solid" :class="`fa-${parseIcon(label.icon).value}`"></i>
                    <span v-else-if="parseIcon(label.icon)" class="lp-emoji">{{ parseIcon(label.icon).value }}</span>
                    <span>{{ label.name }}</span>
                </label>
                <i class="fa-solid fa-pencil lp-edit-icon" @click.stop="openEditForm(label)"></i>
            </div>

            <p class="lp-empty" v-if="!filteredLabels.length">
                {{ $t('dash.posts.labels.no-result') }}
            </p>

            <div class="lp-create-trigger" @click="openCreateForm">
                <i class="fa-solid fa-plus"></i>
                {{ $t('dash.posts.labels.create-new') }}
            </div>
        </div>

        <div class="lp-create-form" v-else>
            <div class="lp-create-back" @click="cancelForm">
                <i class="fa-solid fa-arrow-left"></i>
                {{ $t('dash.posts.labels.back') }}
            </div>

            <p class="lp-create-title">{{ isEditingLabel ? $t('dash.posts.labels.edit-title') : $t('dash.posts.labels.create-new') }}</p>

            <input
                type="text"
                class="lp-create-name"
                v-model="newLabelName"
                :placeholder="$t('dash.posts.labels.name-placeholder')"
                @click.stop
            >

            <div class="lp-create-preview">
                <div class="lp-chip" :style="{ backgroundColor: newLabelColor }">
                    <i v-if="newLabelIconParsed && newLabelIconParsed.type === 'fa'" class="fa-solid" :class="`fa-${newLabelIconParsed.value}`"></i>
                    <span v-else-if="newLabelIconParsed" class="lp-emoji">{{ newLabelIconParsed.value }}</span>
                    <span>{{ newLabelName || $t('dash.posts.labels.name-placeholder') }}</span>
                </div>
            </div>

            <p class="lp-create-subtitle">{{ $t('dash.posts.labels.colors') }}</p>
            <div class="lp-color-row">
                <div
                    class="lp-color-swatch"
                    v-for="color in AUTO_COLORS"
                    :key="color"
                    :style="{ backgroundColor: color }"
                    :class="{ active: newLabelColor === color }"
                    @click="newLabelColor = color; showColorPicker = false"
                ></div>
                <div
                    class="lp-color-swatch lp-color-custom"
                    :class="{ active: showColorPicker }"
                    @click.stop="showColorPicker = !showColorPicker"
                >
                    <i class="fa-solid fa-eye-dropper"></i>
                </div>
            </div>
            <div class="lp-color-picker" v-if="showColorPicker" @click.stop>
                <chrome-picker :value="{ hex: newLabelColor }" @input="v => newLabelColor = v.hex"/>
            </div>

            <p class="lp-create-subtitle">{{ $t('dash.posts.labels.icon') }}</p>
            <div class="lp-icon-trigger" @click.stop="showIconPicker = !showIconPicker">
                <i v-if="newLabelIconParsed && newLabelIconParsed.type === 'fa'" class="fa-solid" :class="`fa-${newLabelIconParsed.value}`"></i>
                <span v-else-if="newLabelIconParsed" class="lp-emoji">{{ newLabelIconParsed.value }}</span>
                <i v-else class="fa-solid fa-icons"></i>
                <span>{{ newLabelIconParsed ? newLabelIconParsed.value : $t('dash.posts.labels.icon-none') }}</span>
            </div>
            <IconPicker
                v-show="showIconPicker"
                :faIcons="LABEL_FA_ICONS"
                :value="newLabelIconParsed || ''"
                @select="onIconSelect"
            />

            <p class="lp-create-error" v-if="createError">{{ createError }}</p>

            <div class="lp-create-actions">
                <button class="lp-btn-cancel" @click="cancelForm">{{ $t('dash.button.cancel') }}</button>
                <button
                    class="lp-btn-submit"
                    :disabled="!newLabelName.trim() || creatingInProgress"
                    @click="submitLabel"
                >{{ isEditingLabel ? $t('dash.posts.labels.save') : $t('dash.posts.labels.create') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Checkbox from '@/components/dash/utils/checkbox.vue'
import IconPicker from '@/components/dash/mediaEditor/components/IconPicker.vue'
import { Chrome } from 'vue-color'
import ClickOutside from 'vue-click-outside'
import LabelsMixin from '@/components/mixins/Labels.vue'
import { parseLabelIcon, encodeLabelIcon } from '@/utils/labelIcon.js'
import { LABEL_FA_ICONS } from './labelFaIconList.js'

const AUTO_COLORS = ['#6B1F4D', '#1a73c9', '#1a8a3e', '#b07a1a', '#7c3aed', '#0891b2', '#be185d', '#0d7377']

export default {
    name: 'LabelPicker',

    components: { Checkbox, IconPicker, 'chrome-picker': Chrome },

    directives: { ClickOutside },

    mixins: [LabelsMixin],

    props: {
        selected: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            search: '',
            creating: false,
            editingLabelId: null,
            newLabelName: '',
            newLabelColor: AUTO_COLORS[0],
            newLabelIcon: '',
            showColorPicker: false,
            showIconPicker: false,
            createError: null,
            creatingInProgress: false,
            AUTO_COLORS,
            LABEL_FA_ICONS
        }
    },

    computed: {
        allLabels() {
            return this.$store.getters['labels/getLabelsList']
        },

        filteredLabels() {
            const q = this.search.trim().toLowerCase()
            if (!q) return this.allLabels
            return this.allLabels.filter(label => label.name.toLowerCase().includes(q))
        },

        isEditingLabel() {
            return !!this.editingLabelId
        },

        newLabelIconParsed() {
            return parseLabelIcon(this.newLabelIcon)
        }
    },

    mounted() {
        // Always refresh (not just when empty) — the store can otherwise stay stuck
        // with whatever was loaded earlier in the session/tab, missing labels created
        // or edited since (e.g. in another tab), until a full page reload.
        this.$store.dispatch('labels/fetchLabels')
    },

    methods: {
        parseIcon(icon) {
            return parseLabelIcon(icon)
        },

        isSelected(labelId) {
            return this.selected.includes(labelId)
        },

        toggleLabel(label) {
            const isCurrentlySelected = this.isSelected(label.id)
            const updated = isCurrentlySelected
                ? this.selected.filter(id => id !== label.id)
                : [...this.selected, label.id]
            this.$emit('update:selected', updated)
        },

        resetForm() {
            this.editingLabelId = null
            this.createError = null
            this.newLabelColor = AUTO_COLORS[Math.floor(Math.random() * AUTO_COLORS.length)]
            this.newLabelIcon = ''
            this.showColorPicker = false
            this.showIconPicker = false
        },

        openCreateForm() {
            this.resetForm()
            this.newLabelName = this.search.trim()
            this.creating = true
        },

        openEditForm(label) {
            this.resetForm()
            this.editingLabelId = label.id
            this.newLabelName = label.name
            this.newLabelColor = label.color
            this.newLabelIcon = label.icon || ''
            this.creating = true
        },

        cancelForm() {
            this.creating = false
            this.editingLabelId = null
        },

        onIconSelect(value) {
            this.newLabelIcon = encodeLabelIcon(value)
            this.showIconPicker = false
        },

        async submitLabel() {
            const name = this.newLabelName.trim()
            if (!name) return

            this.creatingInProgress = true
            this.createError = null

            const payload = {
                name,
                color: this.newLabelColor,
                icon: this.newLabelIcon || null
            }

            const res = this.isEditingLabel
                ? await this.updateLabel({ id: this.editingLabelId, ...payload })
                : await this.newLabel(payload)

            this.creatingInProgress = false

            if (!res || !res.body || !res.body.success) {
                this.createError = res && res.body && res.body.error === 'dash.error.label_name_already_exists'
                    ? this.$t('dash.posts.labels.error-duplicate')
                    : this.$t('dash.posts.labels.error-invalid')
                return
            }

            const savedLabel = res.body.label
            await this.$store.dispatch('labels/fetchLabels')

            if (!this.isEditingLabel) {
                this.$emit('update:selected', [...this.selected, savedLabel.id])
                this.$emit('created', savedLabel)
            }

            this.creating = false
            this.editingLabelId = null
            this.search = ''
        },

        close() {
            this.$emit('close')
        }
    }
}
</script>

<style lang="scss" scoped>
.label-picker {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
    width: 280px;
    padding: 12px;

    .lp-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;

        .lp-title {
            font-weight: 600;
            font-size: 14px;
        }

        i {
            cursor: pointer;
            color: #9ba3b7;
        }
    }

    .lp-search {
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #e3e6ee;
        border-radius: 6px;
        padding: 6px 10px;
        margin-bottom: 10px;

        i {
            color: #9ba3b7;
            font-size: 12px;
        }

        input {
            border: none;
            outline: none;
            flex: 1;
            font-size: 13px;
        }
    }

    .lp-list {
        max-height: 280px;
        overflow-y: auto;
    }

    .lp-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 4px;
        border-radius: 6px;

        &:hover {
            background: #f5f6fa;

            .lp-edit-icon {
                opacity: 1;
            }
        }
    }

    .lp-chip {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 6px;
        color: #fff;
        border-radius: 4px;
        padding: 6px 10px;
        font-size: 13px;
        font-weight: 500;
        min-height: 28px;
        cursor: pointer;

        i {
            font-size: 12px;
        }

        .lp-emoji {
            font-size: 13px;
            line-height: 1;
        }
    }

    .lp-edit-icon {
        color: #9ba3b7;
        font-size: 12px;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.15s;
        padding: 4px;

        &:hover {
            color: #6b1f4d;
        }
    }

    .lp-empty {
        text-align: center;
        color: #9ba3b7;
        font-size: 13px;
        padding: 12px 0;
    }

    .lp-create-trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 4px;
        margin-top: 6px;
        cursor: pointer;
        color: #6b1f4d;
        font-size: 13px;
        border-top: 1px solid #e3e6ee;

        &:hover {
            text-decoration: underline;
        }
    }

    .lp-create-form {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .lp-create-back {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #9ba3b7;
        font-size: 12px;
        cursor: pointer;
        margin-bottom: 4px;
    }

    .lp-create-title {
        font-weight: 600;
        font-size: 13px;
        margin: 0 0 4px;
    }

    .lp-create-name {
        border: 1px solid #e3e6ee;
        border-radius: 6px;
        padding: 8px 10px;
        font-size: 13px;
        outline: none;
    }

    .lp-create-preview {
        margin: 4px 0;
    }

    .lp-create-subtitle {
        font-size: 12px;
        color: #9ba3b7;
        margin: 4px 0 2px;
    }

    .lp-color-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .lp-color-swatch {
        width: 24px;
        height: 24px;
        border-radius: 5px;
        cursor: pointer;
        border: 2px solid transparent;

        &.active {
            border-color: #333;
        }
    }

    .lp-color-custom {
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f5f6fa;
        color: #666;
        font-size: 11px;
    }

    .lp-color-picker {
        margin-top: 4px;
    }

    .lp-icon-trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #e3e6ee;
        border-radius: 6px;
        padding: 8px 10px;
        cursor: pointer;
        font-size: 13px;

        .lp-emoji {
            font-size: 14px;
            line-height: 1;
        }
    }

    .lp-create-error {
        color: #e14e4e;
        font-size: 12px;
    }

    .lp-create-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 6px;

        button {
            border: none;
            border-radius: 6px;
            padding: 8px 14px;
            font-size: 13px;
            cursor: pointer;
        }

        .lp-btn-cancel {
            background: #f5f6fa;
            color: #333;
        }

        .lp-btn-submit {
            background: #6b1f4d;
            color: #fff;

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }
}
</style>
